// AppShell.jsx — top chrome for authenticated pages: AppBar with title, primary
// nav, language toggle, an account menu (email / job post / admin flag / sign
// out), the SB-037 password hint banner, and the content region. The two
// business dialogs (nickname D7, password SB-037) live in their own components
// since SB-042 — the shell only holds their open flags.
//
// 第五十六批：导航条目与门条件全部移到 components/appNav.mjs（纯函数 + 单测）。
// 本文件**不得**再出现第二份路由清单——xs 断点下五条成员入口全部失联的那个洞，
// 根因就是这里曾同时躺着一个 NAV 数组和两个硬写的 MenuItem。桌面 Stack 与移动
// Drawer 现在 map 同一个 navFor(account) 的结果，判据在 appNav.test.mjs 里读本
// 文件源码核对。
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { groupLabel } from '../api/groups.mjs';
import NotificationBell from './NotificationBell.jsx';
import { useLang, useT } from '../i18n/index.mjs';
import NicknameDialog from './NicknameDialog.jsx';
import PasswordDialog from './PasswordDialog.jsx';
import ForcedReadGate from './ForcedReadGate.jsx';
import { canSeeDueActions, DUE_ACTIONS_PATH, isActiveRoute, navFor } from './appNav.mjs';
import api, { currentRevs, onRevChange } from '../api/client.mjs';
import {
  alreadyReloadedFor, IDLE_MS, isEditing, isStale, markReloadedFor, shouldAutoReload,
} from './versionWatch.mjs';
import { dueOnLabel, dueSummary } from './dueBanner.mjs';
import {
  BRAND_TIP_TIMEOUT_MS, brandGlyph, brandTipNeedsTimer, brandTipNext,
} from './shellBrand.mjs';

function initialsOf(account) {
  const src = account?.displayName || account?.email || '?';
  return src.trim().charAt(0).toUpperCase();
}

export default function AppShell({ children }) {
  const t = useT();
  const { lang, toggleLang } = useLang();
  const { account, logout, groups, currentGroup, setCurrentGroup } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const [nickOpen, setNickOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);   // 移动端导航抽屉（xs 断点唯一的导航面）

  // ── 第一百一十批：左上角标识 ──────────────────────────────────────────────
  // 方向按 CSS 的 `orientation` media query（浏览器算、随窗口实时更新），**不是**
  // MUI 断点：断点量的是视口**宽度**，而 768×1024 的平板是 sm 却是纵屏。理由与
  // 字形选择写在 shellBrand.mjs 头注里。
  const portrait = useMediaQuery('(orientation: portrait)');
  const [tipOpen, setTipOpen] = useState(false);

  // SB-037 password hint. Dismissed for THIS session only — deliberately not
  // persisted: the prompt is supposed to come back next login (that is the whole
  // enforcement mechanism for a channel we refuse to make blocking).
  const [pwHintOff, setPwHintOff] = useState(false);
  const needsPassword = Boolean(account) && account.hasPassword === false;
  const needsRotation = Boolean(account) && account.passwordStale === true;
  const showPwHint = !pwHintOff && !pwOpen && (needsPassword || needsRotation);
  useEffect(() => { setPwHintOff(false); }, [account?.id]);

  // ── SB-117（第一百一十三批）：到期动作横幅 ────────────────────────────────
  // 落在**全局壳**而不是某一页：验收判准是「他不去找也会撞见」，而任何单页都
  // 满足不了它——需求方每批经过的页面不是同一张。
  // 判定在 dueBanner.mjs 里（纯函数 + 单测）：本文件不许再躺第二份规则，
  // 理由与本文件头注那条「不得再出现第二份路由清单」逐字同源。
  const [due, setDue] = useState(null);
  const canDue = canSeeDueActions(account);
  useEffect(() => {
    let live = true;
    // 门条件走 appNav 的谓词：壳上不许再有第二份「谁该看见」的判断
    // （判据：`isAdmin` 在 AppShell 里至多出现一次，且那一次是账号菜单上的徽标）。
    if (!canDue) { setDue(null); return undefined; }
    api.dueActions()
      .then((r) => { if (live) setDue(r); })
      // 静默失败：一次网络抖动不该让壳喊一嗓子。**但也绝不因此把已经过期的事藏起来**
      // ——藏起来正是本单要消灭的那种沉默，所以失败时保留上一次的结果。
      .catch(() => {});
    return () => { live = false; };
  }, [account?.id, canDue]);
  const dueInfo = dueSummary(due);

  // ── SB-134（第一百一十四批）：这个标签页跑的还是不是最新那一版 ────────────
  // 判定在 versionWatch.mjs 里（纯函数 + 9 条守卫）。壳里只做三件不可判定的事：
  // 订阅、记最后一次交互、真的调 reload。本文件头注那条纪律同样适用于这里——
  // 规则不许在 JSX 里再写第二份。
  const [stale, setStale] = useState(false);
  const lastInteractionRef = useRef(Date.now());
  useEffect(() => {
    const bump = () => { lastInteractionRef.current = Date.now(); };
    for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart'])
      window.addEventListener(ev, bump, { passive: true });
    return () => {
      for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart'])
        window.removeEventListener(ev, bump);
    };
  }, []);
  useEffect(() => {
    const check = () => {
      const { bootRev, serverRev } = currentRevs();
      const s = isStale(bootRev, serverRev);
      setStale(s);
      if (!s) return;
      // 自动刷的三个条件全在纯函数里判；这里只提供**事实**。
      if (!shouldAutoReload({
        stale: s,
        hidden: document.visibilityState === 'hidden',
        msSinceInteraction: Date.now() - lastInteractionRef.current,
        editing: isEditing(document),
      })) return;
      // 一枚 rev 只自动刷一次：代理层若缓存住旧包，刷了还是旧 ⇒ 无限刷新，
      // 而那比停在旧版坏得多。
      if (alreadyReloadedFor(serverRev, window.sessionStorage)) return;
      markReloadedFor(serverRev, window.sessionStorage);
      window.location.reload();
    };
    const off = onRevChange(check);
    document.addEventListener('visibilitychange', check);
    // 计时器只为"可见但长时间没人碰"那一种；隐藏与新戳都由上面两条事件驱动。
    const timer = setInterval(check, 60_000);
    check();
    return () => { off(); document.removeEventListener('visibilitychange', check); clearInterval(timer); };
  }, []);

  const openNick = () => { setAnchorEl(null); setNickOpen(true); };
  const openPw = () => { setAnchorEl(null); setPwOpen(true); };

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login', { replace: true });
  };

  const isActive = (to) => isActiveRoute(location.pathname, to);

  // 两个导航面**同一个**来源：桌面 Stack 与移动 Drawer 都只 map 这一份。
  const navItems = navFor(account);

  // 第一百零二批：当前组名算一次，两个断点的提示件共用（工具条 Chip / 抽屉头部）。
  // 空串＝没有当前组或组连 slug 都没有 ⇒ 两处都整件不渲染。
  const groupName = groupLabel(currentGroup, lang);

  // 路由一变就收屉。行内的 onClick 已经收过一次，这条不是重复：浏览器前进/后退、
  // 以及通知铃里的跳转都不经过屉内的行，屉会留在原地盖住新页面。
  // 品牌 tips 同理（同一条理由，不同的件）：换页时没有任何 mouseleave/blur 会发生。
  useEffect(() => {
    setNavOpen(false);
    setTipOpen((v) => brandTipNext(v, 'route'));
  }, [location.pathname]);

  // 「超时自动隐藏」。计时器**只在开着时挂**——关着也挂的话，下一次展开会被上一次
  // 遗留的计时器提前关掉（判据在 shellBrand.mjs 的 brandTipNeedsTimer 头注）。
  // 依赖里带上 tipOpen：每次展开都是一次新的计时，而不是从第一次展开起算。
  useEffect(() => {
    if (!brandTipNeedsTimer(tipOpen)) return undefined;
    const h = setTimeout(() => setTipOpen((v) => brandTipNext(v, 'timeout')), BRAND_TIP_TIMEOUT_MS);
    return () => clearTimeout(h);
  }, [tipOpen]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="primary" enableColorOnDark>
        <Toolbar sx={{ gap: 1 }}>
          {/* 汉堡：xs 断点下的导航入口，放在品牌左侧（拇指最先落的位置）。
              本项目不装 @mui/icons-material，用文字字形 ≡（U+2261），同 DocBrowserPage
              的 caret。40×40 = MUI IconButton 的标准触达尺寸。 */}
          <Button
            color="inherit"
            onClick={() => setNavOpen(true)}
            aria-label={t('shell.nav.open')}
            title={t('shell.nav.open')}
            aria-haspopup="dialog"
            aria-expanded={navOpen}
            sx={{
              display: { sm: 'none' },
              minWidth: 40, width: 40, height: 40, px: 0,
              fontSize: 22, lineHeight: 1, flexShrink: 0,
            }}
          >
            <Box component="span" aria-hidden>≡</Box>
          </Button>

          {/* ── 第一百一十批：左上角标识 = 一枚交通灯字形 + 可展开的 tips ──────
              需求方指定：🚦（横屏）/ 🚥（纵屏），点击或 hover 展开 tips 显示
              "Dev Coordinator"，再次互动或超时自动隐藏。方向判据与 tips 状态机在
              components/shellBrand.mjs（含理由：为什么是 orientation media query
              而不是 MUI 断点——断点量的是**宽度**，与横竖屏不是一件事）。

              ⚠ 它**不再是一条到 /docs 的链接**，这是有意的：
                · 一个既导航又要"再点一次收起 tips"的控件，两件事都做不准——点下去
                  页面就换了，气泡随之卸载，"再次互动自动隐藏"在链接上无从实现；
                · /docs 本就是 appNav 单一清单里的第一条，桌面工具条与移动抽屉各有
                  一个入口。品牌上那条手写的 to="/docs" 是**同一路由的第三份副本**，
                  正是 appNav.test.mjs 那条"只此一份清单"判据要防的形状。
              可达性因此不减，反而少了一处要跟着改的事实。

              ── xs 工具条横向预算（重算；px，MUI 默认量：Toolbar 左右留白 16，
                 文本 Button minWidth 64、左右内距各 8，h6=20px/700 罗马字均宽
                 约 0.55em≈11）─────────────────────────────────────────────
                上一版（第五十六批立、第一百零二批复核）：
                  16 + 40(汉堡) + 25("DC") + 8(mr) + 71(通知) + 36(语言) + 48(账号)
                     + 5×8(gap；display:none 的桌面 Stack 与组 Chip 不是 flex item，
                       不占 gap) + 16 ≈ 300 → 320px 视口余 20。
                本批只动品牌那一格：25 → 一枚 emoji。**emoji 的推进宽度依平台差异
                很大**（Windows Segoe UI Emoji / Android Noto Color Emoji / iOS Apple
                Color Emoji，h6=20px 下 23~28px 都可能），所以按**上界 28** 记账，
                而不是按本机量到的那个数：这份预算要在三个平台上同时成立。
                  改后 = 16 + 40 + 28 + 8 + 71 + 36 + 48 + 5×8 + 16 = 303
                         → 320px（SE 一代）余 17；390px 余 87。
                净增 3px。tips 走 MUI Tooltip，它渲染在 Portal 里（脱离 Toolbar 的
                flex 流）⇒ **横向预算增量为 0**——这正是不把提示做成行内 Chip/Collapse
                的原因：那种做法会在展开的瞬间把工具条顶宽，而 AppBar 不在任何滚动
                容器内（见下方 :148 那段），溢出直接落到 body 的横滚条上（DP-11）。
                字形本身 noWrap + flexShrink:0：它只有一个字，压不动也不该被压成
                半个省略号。 */}
          <Tooltip
            open={tipOpen}
            /* 受控：MUI 自己的 hover/focus 监听全部关掉，开合只由 brandTipNext 决定
               ——两套监听并存时，"点击收起"会被 MUI 的 hover 立刻再打开。 */
            disableFocusListener
            disableHoverListener
            disableTouchListener
            placement="bottom-start"
            title={
              <Box>
                <Box sx={{ fontWeight: 700 }}>{t('app.title')}</Box>
                <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>{t('shell.brand.tipDismiss')}</Box>
              </Box>
            }
          >
            <Typography
              variant="h6"
              component="span"
              /* emoji 不是名字：屏幕阅读器读到的必须是产品名，而不是"traffic light"。
                 字形本身 aria-hidden，名字由 aria-label 承担。 */
              role="button"
              tabIndex={0}
              aria-label={t('shell.brand.aria')}
              aria-expanded={tipOpen}
              onMouseEnter={() => setTipOpen((v) => brandTipNext(v, 'enter'))}
              onMouseLeave={() => setTipOpen((v) => brandTipNext(v, 'leave'))}
              onFocus={() => setTipOpen((v) => brandTipNext(v, 'enter'))}
              onBlur={() => setTipOpen((v) => brandTipNext(v, 'leave'))}
              onClick={() => setTipOpen((v) => brandTipNext(v, 'toggle'))}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();               // 空格默认是滚动一屏
                setTipOpen((v) => brandTipNext(v, 'toggle'));
              }}
              noWrap
              sx={{
                color: 'inherit', cursor: 'pointer', userSelect: 'none',
                mr: { xs: 1, sm: 2 }, flexShrink: 0, lineHeight: 1,
              }}
            >
              <Box component="span" aria-hidden>{brandGlyph(portrait)}</Box>
            </Typography>
          </Tooltip>

          {/* sm 断点的横向预算（第五十九批补算，沿用上面那套量；**第一百一十批重算**）：
                上一版：600px 视口 = 24×2 留白 + 品牌(可缩至省略号) + mr 16 + 通知 71
                        + 语言 64 + 账号 64 + 5×8 gap ≈ 303 固定占用（品牌按最坏情形
                        计 0）→ 留给 tab 栏 ≈ 267；第一百零二批的组 Chip 又拿走 128。
                本批品牌从 "Dev Coordinator"（自然宽 ≈165、可压成省略号）换成**一枚
                不可压缩的 emoji（上界 28）**，两个方向都要说清：
                  · **典型情形省下 ~137px**：此前这一行在 sm..md 早已超预算，品牌与
                    tab 栏按 flex 规则一起被压，品牌实占常在 100~165 之间；现在它恒
                    占 28，那 100+ 全部还给 tab 栏那条 overflowX 容器。
                  · **最坏情形多占 28px**：品牌此前理论上可被压到近 0（省略号），
                    现在压不动了。于是固定占用上界 = 48 + 28 + 16 + 71 + 64 + 64 + 40
                    = 331，加组 Chip 128 → 459，600px 下留给 tab 栏 ≈ 141。
                后一条**不会**变成 body 溢出：tab 栏自第五十九批起就在自己的
                overflowX 容器里（minWidth:0 + overflowX:auto），多出来的部分在这一行
                内部横滚——这正是 DP-11 的处方原文。所以这次改动在 sm 上的净效果是
                "tab 栏通常多 137px 可视宽度、最坏多滚 28px"，两种都不碰 body。
                而 tab 栏的**最小**宽度是硬的：MUI 文本 Button 的 minWidth 下限 64，
                成员六条 = 6×64 + 5×4(spacing 0.5) = 404；英文标签更宽（Submit ticket /
                My submissions 各超过 110），实测口径下六条接近 600。
              也就是说这一行在 sm..md 早已超出预算，而 AppBar 不在任何滚动容器内——
              它的溢出会直接变成 **body 的横滚条**（禁横滚铁律 DP-11）。第五十六批只算了
              xs（那里桌面 Stack 是 display:none，不占位），sm 这一段一直没人算。
              本批新增手册入口把净增量又推高一格，所以在加这条入口的同一次改动里把这一行
              封住：minWidth:0 让它可以被压缩，overflowX 让多出来的部分**在这一行内部**
              横滚（DP-11 的处方原文），按钮自身 flexShrink:0 + nowrap 以免标签被压成两行。
              宽屏有富余时不会出现滚动条，桌面观感不变。 */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              flexGrow: 1,
              display: { xs: 'none', sm: 'flex' },
              minWidth: 0,
              overflowX: 'auto',
              scrollbarWidth: 'thin',
            }}
          >
            {navItems.map((item) => (
              <Button
                key={item.to}
                component={RouterLink}
                to={item.to}
                color="inherit"
                sx={{
                  fontWeight: isActive(item.to) ? 700 : 400,
                  opacity: isActive(item.to) ? 1 : 0.85,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {t(item.key)}
              </Button>
            ))}
          </Stack>

          <Box sx={{ flexGrow: { xs: 1, sm: 0 } }} />

          <NotificationBell />

          <Button
            color="inherit"
            onClick={toggleLang}
            aria-label={t('app.langToggle.aria')}
            sx={{ minWidth: { xs: 'auto', sm: 64 }, flexShrink: 0 }}
          >
            {t('app.langToggle')}
          </Button>

          {/* ── 第一百零二批：当前项目组常驻提示 ─────────────────────────────
              需求方本批建议"账号头像旁边放一枚小 Chip"。三条落法各有代价：
                · **只在 sm 起渲染**。xs 的横向预算在上面那笔账里只剩约 20px（320px
                  视口），任何常驻件都会把溢出推回 body（DP-11 铁律）——所以 xs 改把
                  组名放进导航抽屉的头部（见下方 Drawer），那里是纵向的，加一行净增 0。
                · **多组才可点**。点它＝开账号菜单（切换器就在里面，第一百零一批放的
                  位置），所以这里不新做第二个切换面：两个切换面就是两份要跟着改的
                  事实。单组时它只是提示，onClick 缺席 ⇒ MUI 不给它按钮语义/悬浮态，
                  用户不会去点一个点了没反应的东西。
                · currentGroup 为空则整件不渲染（groupLabel 返回空串也一样）——一枚
                  空 Chip 说不出任何事情，只会让人以为组名没加载出来。
              sm 的账（沿用上面那套量）：净增量 = maxWidth 120 + 一条 gap 8 = 128px，
              且它 flexShrink:1 可再缩。这 128 全部落在 tab 栏那条 overflowX 容器的
              可用宽度上（第五十九批给它加了 minWidth:0，会跟着缩），换来的是那一行
              多滚一点，而**不是** body 溢出；md 起放宽到 180（那里横向有富余）。
              MUI Chip 的 label 自带 nowrap+省略号：组名是用户填的，长名只准变省略号，
              绝不许把工具条顶宽（同品牌那道兜底）。 */}
          {groupName ? (
            <Chip
              size="small"
              variant="outlined"
              label={groupName}
              aria-label={t('shell.group.aria', { name: groupName })}
              title={groups.length > 1
                ? t('shell.group.switch')
                : t('shell.group.aria', { name: groupName })}
              {...(groups.length > 1
                ? {
                  onClick: (e) => setAnchorEl(e.currentTarget),
                  'aria-haspopup': 'menu',
                  'aria-expanded': menuOpen,
                }
                : {})}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                maxWidth: { sm: 120, md: 180 },
                flexShrink: 1,
                minWidth: 0,
                color: 'inherit',
                borderColor: 'currentColor',
                opacity: 0.9,
              }}
            />
          ) : null}

          <Button
            color="inherit"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            startIcon={
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main', fontSize: 14 }}>
                {initialsOf(account)}
              </Avatar>
            }
            sx={{ textTransform: 'none', minWidth: { xs: 'auto', sm: 64 }, flexShrink: 0 }}
          >
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
              {account?.email}
            </Box>
          </Button>

          <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)} keepMounted>
            <MenuItem disabled sx={{ opacity: 1 }}>
              <ListItemText
                primary={account?.displayName || account?.email}
                secondary={account?.email}
              />
            </MenuItem>
            <MenuItem disabled sx={{ opacity: 1 }}>
              <ListItemText
                primary={`${t('shell.account.jobPost')}: ${
                  (lang === 'en'
                    ? account?.jobPostNameEn || account?.jobPostKey
                    : account?.jobPostKey) || '—'
                }`}
              />
              {account?.isAdmin ? (
                <Chip size="small" color="secondary" label={t('shell.account.admin')} sx={{ ml: 1 }} />
              ) : null}
            </MenuItem>
            {/* ── 第一百零一批（多项目制）：项目切换 ──────────────────────────
                放账号菜单而不是工具条：xs 工具条横向预算改前只剩 ~20px（上方那笔
                账），任何新常驻件都会把溢出推回 body。单组成员整段不渲染——
                需求方裁「单一项目权限的成员不需要选择或切换」，判据就是列表长度。
                选中项打勾字形 ✓（本项目不装 icons 包）。 */}
            {groups.length > 1 ? <Divider /> : null}
            {groups.length > 1 ? (
              <MenuItem disabled sx={{ opacity: 1 }}>
                <ListItemText primary={t('shell.group.title')} />
              </MenuItem>
            ) : null}
            {groups.length > 1 ? groups.map((g) => (
              <MenuItem
                key={g.id}
                selected={currentGroup?.id === g.id}
                onClick={() => { setCurrentGroup(g); setAnchorEl(null); }}
              >
                <Box component="span" aria-hidden
                     sx={{ width: 20, flexShrink: 0 }}>
                  {currentGroup?.id === g.id ? '✓' : ''}
                </Box>
                {groupLabel(g, lang)}
              </MenuItem>
            )) : null}
            <Divider />
            <MenuItem onClick={openNick}>{t('shell.account.editNickname')}</MenuItem>
            <MenuItem onClick={openPw}>
              {account?.hasPassword ? t('shell.account.changePassword')
                                    : t('shell.account.setPassword')}
            </MenuItem>
            {/* 这里曾硬写着"审批"与"账号管理"两条移动端入口——它们是 xs 断点下
                仅有的导航，而对投资方用户恰恰最不相关。现已随第二份路由清单一并
                删除，全部入口统一走左侧抽屉。 */}
            <MenuItem onClick={handleLogout}>{t('shell.account.logout')}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* 移动导航抽屉。Drawer 在本仓已有成例（DocHistoryDrawer）。
          sm 起整个抽屉 display:none —— 桌面尺寸下 tab 栏已经全量呈现，抽屉再留着
          就成了第二条并行入口（也是 MUI 响应式抽屉的官方写法）。 */}
      <Drawer
        anchor="left"
        open={navOpen}
        onClose={() => setNavOpen(false)}
        sx={{ display: { xs: 'block', sm: 'none' } }}
        slotProps={{ paper: { sx: { width: 272, maxWidth: '80vw' } } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography sx={{ fontWeight: 700 }} variant="subtitle1" noWrap>
            {t('shell.nav.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {account?.displayName || account?.email || ''}
          </Typography>
          {/* 第一百零二批：xs 断点的"当前项目组"就落在这里。工具条那枚 Chip 在 xs
              被 display:none 摘掉（横向预算见上方那笔账），而"我现在在看哪个组"是
              这一批新增的汇报读面与既有工作项/工时页共同的前提——xs 用户不该只能
              靠翻开账号菜单才知道。抽屉是纵向的，多一行不花任何横向预算。
              这里**不做**切换：切换器在账号菜单里，两个切换面就是两份要跟着改的
              事实（第五十六批为并行入口付过的账）。 */}
          {groupName ? (
            <Chip
              size="small"
              variant="outlined"
              label={groupName}
              aria-label={t('shell.group.aria', { name: groupName })}
              sx={{ mt: 0.75, maxWidth: '100%' }}
            />
          ) : null}
        </Box>
        {/* 与桌面 Stack 同一个 navItems：任何入口的增删/门条件都只改 appNav.mjs
            一处，两个面自动同步——这正是本批修复的判据。 */}
        <List sx={{ py: 0.5 }}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={isActive(item.to)}
              onClick={() => setNavOpen(false)}
              sx={{ minHeight: 48 }}
            >
              <ListItemText
                primary={t(item.key)}
                slotProps={{ primary: { fontWeight: isActive(item.to) ? 700 : 400 } }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {/* Fluid width (2026-07-28 owner feedback: the lg cap wasted big side
          gutters on wide screens); pages that want a readable column cap
          themselves (e.g. SubmitTicketPage). */}
      <Container component="main" maxWidth={false} sx={{ flexGrow: 1, py: 3, px: { xs: 2, sm: 3 } }}>
        {children}
      </Container>

      {/* SB-134 新版本横幅。**排在最上面**：一个跑着旧 JS 的页面，它显示的**其余一切**
          都可能是过时的——包括下面那条到期动作横幅。所以这一条要先说。
          与到期动作那条同样**不可关闭**，理由不同：那一条关掉之后事情没人再提起；
          这一条关掉之后，人会带着一个"看起来正常、实际过时"的页面继续工作。 */}
      {stale ? (
        <Container maxWidth={false} sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                {t('shell.update.reload')}
              </Button>
            }
          >
            {t('shell.update.hint')}
          </Alert>
        </Container>
      ) : null}

      {/* SB-117 到期动作横幅。**刻意不可关闭**——口令提示关掉之后验证码登录照样能进，
          没有人会被挡在门外；而到期动作关掉之后，那件事就真的没有任何东西会再提起它了。
          排在口令提示**之前**：一件今天该做而没做的事，比"该设个口令了"急。 */}
      {dueInfo.severity ? (
        <Container maxWidth={false} sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
          <Alert
            severity={dueInfo.severity}
            action={
              <Button color="inherit" size="small" component={RouterLink} to={DUE_ACTIONS_PATH}>
                {t('shell.due.open')}
              </Button>
            }
          >
            {dueInfo.kind === 'overdue'
              ? t('shell.due.overdue', { n: dueInfo.count, days: dueInfo.worst?.daysLate ?? 0,
                                         title: dueInfo.worst?.title ?? '' })
              : t('shell.due.today', { n: dueInfo.count, title: dueInfo.worst?.title ?? '' })}
            {/* SB-191：句尾带上这一行的帧与两个今天——"迟了 N 天"是按哪个午夜算的，读的人要看得见。
                标记规则在 dueBanner.mjs（dueOnLabel），壳里只拼句子；旧服务端没给 todayUtc 就不加这半句。 */}
            {dueInfo.todayUtc
              ? ` ${t('shell.due.frame', { dueOn: dueOnLabel(dueInfo.worst),
                                           utc: dueInfo.todayUtc, local: dueInfo.todayLocal ?? '' })}`
              : null}
          </Alert>
        </Container>
      ) : null}

      {/* SB-037 password hint banner (set / rotate). */}
      {showPwHint ? (
        <Container maxWidth={false} sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
          <Alert
            severity={needsRotation ? 'warning' : 'info'}
            onClose={() => setPwHintOff(true)}
            action={
              <Button color="inherit" size="small" onClick={openPw}>
                {needsRotation ? t('shell.pw.rotateNow') : t('shell.pw.setNow')}
              </Button>
            }
          >
            {/* Rotation is enforced by expiring the PASSWORD CHANNEL, never by
                blocking the app: code login always works, so this prompt can be
                dismissed without stranding anybody. */}
            {needsRotation ? t('shell.pw.staleHint') : t('shell.pw.absentHint')}
          </Alert>
        </Container>
      ) : null}

      <NicknameDialog open={nickOpen} onClose={() => setNickOpen(false)} />
      <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />

      {/* ── SB-146（第一百二十二批）：站内信强读闸 + 直登清欠链 ─────────────────
          落在**全局壳**的理由与 SB-117 横幅同一条：判准是「登录/刷新落地即拦、
          每次路由切换前拦」，而任何单页都满足不了它。触发/查询节流/放行判定全在
          ForcedReadGate 里（判定入 .mjs 配单测）；铃铛已有的 60s 轮询不动。 */}
      <ForcedReadGate />
    </Box>
  );
}
